import { useState, useEffect, useCallback } from 'react';
import {
  Store,
  FileText,
  Save,
  CheckCircle,
  XCircle,
  Loader2,
  Settings as SettingsIcon,
  Sparkles,
  Eye,
  EyeOff,
  ExternalLink,
  Bot,
  Check,
  AlertCircle,
  Database,
  Download,
  UploadCloud,
  ImagePlus,
  FileArchive,
  FolderArchive,
  RefreshCw,
  X,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '../config/api';
import syncService from '../services/syncService';
import SearchableSelect from '../components/SearchableSelect';
import { useSettings } from '../context/SettingsContext';

export default function Settings() {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [popup, setPopup] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingApi, setTestingApi] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [exportingBackup, setExportingBackup] = useState(false);
  const [importingBackup, setImportingBackup] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const { settings, loading, updateSetting, saveSettings, refresh } = useSettings();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleTestApi = async () => {
    if (!settings.gemini_api_key?.trim()) {
      showPopup('Please enter a Google Gemini API key first', 'error');
      return;
    }
    setTestingApi(true);
    setTestResult(null);
    try {
      const res = await api.post('/ai/test', {
        gemini_api_key: settings.gemini_api_key.trim(),
        gemini_model: settings.gemini_model || 'gemini-1.5-flash',
      });
      setTestResult({ success: true, message: res?.message || 'Connected successfully to Google Gemini!' });
      showPopup('Gemini API connection verified successfully!');
    } catch (err) {
      setTestResult({ success: false, message: err.message || 'Connection failed' });
      showPopup(err.message || 'Connection failed', 'error');
    } finally {
      setTestingApi(false);
    }
  };

  const handleExportBackup = async () => {
    setExportingBackup(true);
    try {
      await api.exportBackup();
      showPopup('Backup archive downloaded successfully!');
    } catch (err) {
      showPopup(err.message || 'Failed to export backup', 'error');
    } finally {
      setExportingBackup(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        showPopup('Please select a valid .ZIP backup archive file', 'error');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        showPopup('Please select a valid .ZIP backup archive file', 'error');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImportBackup = async () => {
    if (!selectedFile) {
      showPopup('Please choose a .ZIP backup file to import', 'error');
      return;
    }
    setShowConfirmModal(false);
    setImportingBackup(true);
    setImportResult(null);
    try {
      const res = await api.importBackup(selectedFile);
      setImportResult(res);
      showPopup('Database backup restored successfully! Database synchronized.');
      setSelectedFile(null);
      // Completely reset and re-sync local IndexedDB mirror
      await syncService.resetAndPullFromServer();
      await refresh();
    } catch (err) {
      showPopup(err.message || 'Failed to restore backup archive', 'error');
    } finally {
      setImportingBackup(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings(settings);
      setSaved(true);
      showPopup('Settings saved successfully!');
      setTimeout(() => setSaved(false), 3000);
    } catch {
      showPopup('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      showPopup('Please select a PNG, JPEG, WebP, or GIF logo image', 'error');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showPopup('Logo image must be 2 MB or smaller', 'error');
      return;
    }
    setUploadingLogo(true);
    try {
      const result = await api.uploadShopLogo(file);
      updateSetting('pharmacy_logo', result.pharmacy_logo);
      await refresh();
      showPopup('Shop logo updated successfully!');
    } catch (err) {
      showPopup(err.message || 'Failed to upload logo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {popup && (
        <div
          className={clsx(
            'fixed top-5 right-5 z-50 flex items-center gap-3 rounded-xl border px-5 py-3.5 shadow-lg transition-all',
            popup.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-red-200 bg-red-50 text-red-700'
          )}
        >
          {popup.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-emerald-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <p className="text-sm font-semibold">{popup.message}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Shop Settings</h1>
      </div>

      {/* Shop Details */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Shop Details</h2>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Shop Name</label>
            <input
              value={settings.pharmacy_name}
              onChange={(e) => updateSetting('pharmacy_name', e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Shop Logo</label>
            <div className="flex flex-wrap items-center gap-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                {settings.pharmacy_logo ? (
                  <img src={settings.pharmacy_logo} alt="Current shop logo" className="h-full w-full object-contain" />
                ) : (
                  <ImagePlus className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700">
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  {uploadingLogo ? 'Uploading...' : 'Upload / Change Logo'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleLogoChange} disabled={uploadingLogo} />
                </label>
                <p className="mt-1.5 text-xs text-gray-500">PNG, JPEG, WebP, or GIF — maximum 2 MB. Used on the POS and printed documents.</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Shop Phone</label>
              <input
                value={settings.pharmacy_phone}
                onChange={(e) => updateSetting('pharmacy_phone', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Shop Email</label>
              <input
                type="email"
                value={settings.pharmacy_email}
                onChange={(e) => updateSetting('pharmacy_email', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Complete Shop Address</label>
            <textarea
              value={settings.pharmacy_address}
              onChange={(e) => updateSetting('pharmacy_address', e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
            />
          </div>
        </div>
      </div>

      {/* Invoice & Operational Settings */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
            <FileText className="h-4 w-4 text-emerald-600" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">Invoice & Operational Settings</h2>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-5 gap-5">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Currency</label>
              <input
                value={settings.currency}
                onChange={(e) => updateSetting('currency', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-semibold focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Invoice Prefix</label>
              <input
                value={settings.invoice_prefix}
                onChange={(e) => updateSetting('invoice_prefix', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-semibold focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Default Tax (%)</label>
              <div className="relative">
                <input
                  type="number"
                  value={settings.tax_rate}
                  onChange={(e) => updateSetting('tax_rate', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Low Stock Alert At</label>
              <input
                type="number"
                value={settings.low_stock_threshold}
                onChange={(e) => updateSetting('low_stock_threshold', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Expiry Alert (Days)</label>
              <input
                type="number"
                value={settings.expiry_alert_days}
                onChange={(e) => updateSetting('expiry_alert_days', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2 border-t border-gray-100">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Thermal Receipt Font</label>
              <select
                value={settings.receipt_font || 'consolas'}
                onChange={(e) => updateSetting('receipt_font', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
              >
                <option value="consolas">Consolas (Crisp Mono - Recommended for POS)</option>
                <option value="segoe">Segoe UI / Arial (Clean Thermal Sans - High Clarity)</option>
                <option value="roboto-mono">Roboto Mono (Modern POS Monospace)</option>
                <option value="lucida">Lucida Console (Classic Bold POS)</option>
                <option value="courier">Courier New (Legacy Typewriter)</option>
              </select>
              <p className="mt-1 text-[11px] text-gray-500">
                Consolas and Segoe UI have thicker uniform strokes that eliminate blurry, broken, or faint thermal prints.
              </p>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Thermal Print Darkness / Weight</label>
              <select
                value={settings.receipt_font_weight || 'bold'}
                onChange={(e) => updateSetting('receipt_font_weight', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
              >
                <option value="bold">Crisp & Dark (Bold - Recommended for 203 DPI)</option>
                <option value="normal">Normal</option>
              </select>
              <p className="mt-1 text-[11px] text-gray-500">
                Produces 2-dot thick character stems for ultra-sharp prints without dithering on thermal rolls.
              </p>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Receipt Footer Message</label>
            <textarea
              value={settings.receipt_footer}
              onChange={(e) => updateSetting('receipt_footer', e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
            />
          </div>

          {/* Receipt Font Live Sample Preview */}
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Receipt Font Live Preview</p>
            <div
              className={`p-3 bg-white rounded border border-gray-200 text-black max-w-[320px] shadow-sm text-[11px] leading-tight ${
                (settings.receipt_font_weight || 'bold') !== 'normal' ? 'font-semibold' : 'font-normal'
              }`}
              style={{
                fontFamily:
                  (settings.receipt_font === 'segoe'
                    ? "'Segoe UI', Arial, sans-serif"
                    : settings.receipt_font === 'roboto-mono'
                    ? "'Roboto Mono', monospace"
                    : settings.receipt_font === 'lucida'
                    ? "'Lucida Console', monospace"
                    : settings.receipt_font === 'courier'
                    ? "'Courier New', monospace"
                    : "'Consolas', 'Lucida Console', monospace"),
              }}
            >
              <div className="text-center pb-1 border-b border-black font-black uppercase tracking-wider">
                {settings.pharmacy_name || 'Gaza Pharmacy'}
              </div>
              <div className="py-1 text-[10.5px] border-b border-dashed border-black">
                <div className="flex justify-between">
                  <span>INVOICE:</span>
                  <span className="font-bold">GZ-20260907-0001</span>
                </div>
                <div className="flex justify-between">
                  <span>PANADOL 500MG x2</span>
                  <span className="font-bold">Rs. 160.00</span>
                </div>
              </div>
              <div className="pt-1 flex justify-between font-black text-xs">
                <span>NET TOTAL:</span>
                <span>Rs. 160.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Assistant & Google Gemini Configuration */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
              <Sparkles className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">AI Assistant & Google Gemini Configuration</h2>
              <p className="text-xs text-gray-500">Power intelligent pharmacy analytics, low-stock insights, and drug knowledge using Gemini AI</p>
            </div>
          </div>
          <a
            href="https://aistudio.google.com/app/apikey"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
          >
            <span>Get free API key</span>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="p-6 space-y-5">
          {/* AI Assistant On/Off Toggle */}
          <div className="mb-5 flex items-center justify-between p-4 rounded-lg bg-emerald-50 border border-emerald-100">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                <Sparkles className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">AI Assistant</p>
                <p className="text-xs text-gray-500">Enable or disable AI-powered pharmacy insights</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => updateSetting('enable_ai_assistant', settings.enable_ai_assistant === '1' ? '0' : '1')}
              className={clsx(
                'relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
                settings.enable_ai_assistant === '1'
                  ? 'bg-emerald-600 border-emerald-600'
                  : 'bg-gray-200 border-gray-200 hover:bg-gray-300'
              )}
              role="switch"
              aria-checked={settings.enable_ai_assistant === '1'}
              aria-label="Toggle AI Assistant"
            >
              <span
                className={clsx(
                  'inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200',
                  settings.enable_ai_assistant === '1' ? 'translate-x-6' : 'translate-x-0'
                )}
                aria-hidden="true"
              />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <label className="mb-1.5 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-gray-500">
                <span>Google Gemini API Key</span>
                <span className="text-[11px] font-normal lowercase text-gray-400">Stored securely in database</span>
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={settings.gemini_api_key || ''}
                  onChange={(e) => updateSetting('gemini_api_key', e.target.value)}
                  placeholder="AIzaSy..."
                  className={settings.enable_ai_assistant === '1'
                    ? "w-full rounded-lg border border-gray-200 bg-gray-50 pl-4 pr-10 py-2.5 text-sm font-mono focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    : "w-full rounded-lg border border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed py-2.5"}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className={settings.enable_ai_assistant === '1'
                    ? "absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    : "absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-not-allowed"}
                  title={showApiKey ? 'Hide API key' : 'AI Assistant is disabled'}
                >
                  {settings.enable_ai_assistant === '1'
                    ? showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />
                    : <XCircle className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Gemini Model</label>
              <SearchableSelect
                value={settings.gemini_model || 'gemini-1.5-flash'}
                onChange={(val) => updateSetting('gemini_model', val || 'gemini-1.5-flash')}
                options={[
                  { value: 'gemini-1.5-flash', label: 'gemini-1.5-flash (Standard Flash)' },
                  { value: 'gemini-1.5-flash-latest', label: 'gemini-1.5-flash-latest' },
                  { value: 'gemini-2.0-flash', label: 'gemini-2.0-flash (Recommended / Fast)' },
                  { value: 'gemini-1.5-pro', label: 'gemini-1.5-pro (High Reasoning)' },
                  { value: 'gemini-1.5-pro-latest', label: 'gemini-1.5-pro-latest' },
                ]}
                placeholder="Select Gemini Model"
                searchPlaceholder="Search model..."
                disabled={settings.enable_ai_assistant !== '1'}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleTestApi}
                disabled={testingApi || !settings.gemini_api_key?.trim() || settings.enable_ai_assistant !== '1'}
                className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {testingApi ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {testingApi ? 'Testing Connection...' : 'Test Connection'}
              </button>

              {testResult && (
                <div
                  className={clsx(
                    'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border',
                    testResult.success
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  )}
                >
                  {testResult.success ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                  )}
                  <span>{testResult.message}</span>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400">
              Changes take effect immediately once saved.
            </p>
          </div>
        </div>
      </div>

      {/* Database Backup & Restore */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
              <Database className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Database Data Backup & Restore</h2>
              <p className="text-xs text-gray-500">Export complete database tables to a ZIP archive, or restore system state to a backup snapshot</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Export Column */}
            <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-5 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                    <Download className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Export Database Backup</h3>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Generates a full backup containing every database table (<code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] text-gray-700">medicines.json</code>, <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] text-gray-700">sales.json</code>, <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] text-gray-700">categories.json</code>, <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] text-gray-700">customers.json</code>, etc.) compressed inside a single <strong className="text-gray-800">.ZIP</strong> archive.
                </p>
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span>Each table formatted in clean, human-readable JSON</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span>Includes metadata manifest with timestamp & table counts</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span>Instant direct browser download</span>
                  </div>
                </div>
              </div>

              <div className="pt-5 mt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleExportBackup}
                  disabled={exportingBackup}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {exportingBackup ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Creating Backup Archive...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <span>Export & Download Backup (.ZIP)</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Import Column */}
            <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-5 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-100 text-blue-700">
                    <UploadCloud className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Import & Restore Database</h3>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Upload a backup <strong className="text-gray-800">.ZIP</strong> archive to restore your database back to the snapshot state when that backup was taken.
                </p>

                {/* Dropzone */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  className={clsx(
                    'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors cursor-pointer',
                    dragActive
                      ? 'border-blue-500 bg-blue-50/50'
                      : selectedFile
                      ? 'border-emerald-400 bg-emerald-50/30'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50/50'
                  )}
                  onClick={() => document.getElementById('backup-file-input')?.click()}
                >
                  <input
                    id="backup-file-input"
                    type="file"
                    accept=".zip,application/zip"
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  {selectedFile ? (
                    <div className="flex items-center justify-between w-full px-2 py-1">
                      <div className="flex items-center gap-3 text-left overflow-hidden">
                        <FolderArchive className="h-8 w-8 text-emerald-600 shrink-0" />
                        <div className="truncate">
                          <p className="text-xs font-semibold text-gray-900 truncate">{selectedFile.name}</p>
                          <p className="text-[11px] text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          setImportResult(null);
                        }}
                        className="rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                        title="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1 py-2">
                      <FileArchive className="mx-auto h-7 w-7 text-gray-400" />
                      <p className="text-xs font-medium text-gray-700">
                        Drop backup <span className="font-semibold text-blue-600">.zip</span> here or click to browse
                      </p>
                      <p className="text-[11px] text-gray-400">Accepts ZIP backup files containing table JSONs</p>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-amber-50/70 border border-amber-200/60 p-2.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 leading-tight">
                    <strong>Full State Restore:</strong> Restoring this backup will overwrite existing database records and revert cash, balances, and inventory to the backup point in time.
                  </p>
                </div>
              </div>

              <div className="pt-4 mt-3 border-t border-gray-100 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={importingBackup || !selectedFile}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importingBackup ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Restoring Database...</span>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="h-4 w-4" />
                      <span>Restore Database State</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Import Results Card */}
          {importResult && (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/40 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <Check className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">Database Restoration Completed</h4>
                    <p className="text-xs text-gray-600">Database tables and local cache synchronized successfully.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setImportResult(null)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-emerald-100 hover:text-gray-700 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Restored Tables</span>
                  <p className="text-base font-bold text-gray-900">{importResult?.summary?.total_tables || 0}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-3 text-center">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total In Backup</span>
                  <p className="text-base font-bold text-gray-900">{importResult?.summary?.total_records || 0}</p>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Restored Records</span>
                  <p className="text-base font-bold text-emerald-700">{importResult?.summary?.restored_records ?? importResult?.summary?.inserted_records ?? 0}</p>
                </div>
              </div>

              {Array.isArray(importResult?.details) && importResult.details.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden max-h-56 overflow-y-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase text-[10px] tracking-wider sticky top-0">
                      <tr>
                        <th className="px-4 py-2">Table</th>
                        <th className="px-4 py-2 text-right">In Backup</th>
                        <th className="px-4 py-2 text-right">Restored</th>
                        <th className="px-4 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700">
                      {importResult.details.map((row) => (
                        <tr key={row.table} className="hover:bg-gray-50/50">
                          <td className="px-4 py-1.5 font-mono text-gray-900">{row.table}</td>
                          <td className="px-4 py-1.5 text-right font-medium">{row.total}</td>
                          <td className="px-4 py-1.5 text-right font-medium text-emerald-600">{row.restored ?? row.inserted}</td>
                          <td className="px-4 py-1.5 text-center font-medium text-emerald-600">Restored</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Confirm Database Restoration</h3>
                <p className="text-xs text-gray-500">Roll back database to backup snapshot</p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3.5 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Backup File:</span>
                <span className="font-semibold text-gray-900 truncate max-w-[200px]">{selectedFile.name}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">File Size:</span>
                <span className="font-semibold text-gray-900">{(selectedFile.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 leading-relaxed">
              <strong className="text-rose-600">Warning:</strong> Restoring this backup will replace current database tables with the data from this archive. Any sales, payments, purchases, or changes made <strong>after</strong> this backup was created will be rolled back.
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImportBackup}
                className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 shadow-sm transition-colors"
              >
                Yes, Restore Database
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
}
